import React, { useState, useEffect, useRef } from 'react';

import { useCookieWatcher } from './useCookieWatcher';
import { useNotifications } from './useNotifications';
import { useConfirm } from './useConfirm';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { Button } from 'react-bootstrap';

const notificationMilliSeconds = 60000; // 1 min

function SessionCountdown() {
  const { localize } = useLocalization();
  const { notify } = useNotifications();
  const { logout, getUser, loading } = useAuth();
  const confirm = useConfirm();

  const [countdown, setCountdown] = useState(notificationMilliSeconds / 1000);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const continueSession = () => {
    getUser()
      .then(() => {
        notify('user.session.continued');
        confirm.close();
      })
      .catch(logout);
  }

  return (
    <>
      <div>
        {localize('user.session.expires.in', {
          values: {
            time: countdown.toFixed(0)
          }
        })}
      </div>
      <div className='d-flex justify-content-end mx-2 mb-0 mt-2'>
        <Button
          size='sm'
          variant='danger'
          disabled={loading}
          onClick={continueSession}
        >
          {localize('user.session.continue')}
        </Button>
      </div>
    </>
  );
}

export const useSession = () => {
  const { logout, isLoggedIn } = useAuth();
  const { localize } = useLocalization();
  const { confirm } = useConfirm();

  const sessionTimer = useCookieWatcher('sessionTimer');

  const notificationTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);

  const createWarningNotification = () => {
    confirm({
      rootClose: false,
      cancelButton: false,
      header: localize('user.session.expires'),
      body: <SessionCountdown />
    });
  };

  // Change in sessionTimer cookie triggers timers to start over
  useEffect(() => {
    if (!isLoggedIn || !sessionTimer) return;

    // Example of cookie = 200%3A1800000 (decoded as 200:1800000)
    // 200 shows the amount of api calls
    // 1800000 is the session lenght in milliseconds -> 30 min
    const sessionTime = decodeURIComponent(sessionTimer)?.split(':')[1];
    if (!sessionTime || isNaN(sessionTime)) return;

    // add 2s buffer before the actual session in backend expires
    const timeLeft = Number(sessionTime) - 2000;

    notificationTimerRef.current = setTimeout(createWarningNotification, timeLeft - notificationMilliSeconds);
    logoutTimerRef.current = setTimeout(logout, timeLeft);

    return () => {
      clearTimeout(notificationTimerRef.current);
      clearTimeout(logoutTimerRef.current);
    }
  }, [isLoggedIn, sessionTimer]);
};
